import { faBrain, faRobot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';
import { askCemadeIA } from '../../services/callAPI/toOpenAI';

const AssistantCemade: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [question, setQuestion] = useState<string>('');
  const [_response, setResponse] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [history, setHistory] = useState<{ from: 'user' | 'ia', text: string }[]>([]);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const handleSend = async () => {
    if (!question.trim()) return;

    setIsLoading(true);
    setResponse('');

    // Ajoute la question à l'historique
    setHistory((prev) => [...prev, { from: 'user', text: question }]);
    setQuestion('');

    try {
      //ask to open IA
      const reply = await askCemadeIA(question);

      // Ajoute la réponse à l'historique
      setHistory((prev) => [...prev, { from: 'ia', text: reply }]);
      setResponse('');
    } catch (error: any) {
      const errorMessage = "Une erreur est survenue lors de la réponse de l’assistant.";
      setHistory((prev) => [...prev, { from: 'ia', text: errorMessage }]);
      setResponse('');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history, isLoading]);


  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Bouton flottant */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-orangeCustom text-white font-semibold px-4 py-2 rounded-full shadow-lg hover:opacity-90 transition"
        >
          <p><FontAwesomeIcon icon={faRobot} /> Assistant IA</p>
        </button>
      )}

      {/* Boîte de chat */}
      {isOpen && (
        <div className="w-80 max-w-[90vw] min-w-[280px] h-[400px] min-h-[360px] bg-white border border-gray-300 rounded-xl shadow-xl flex flex-col overflow-hidden">
          {/* En-tête */}
          <div className="bg-greenCustom text-white px-4 py-2 flex justify-between items-center">
            <span className="font-semibold">Assistant Cemade IA</span>
            <button onClick={() => setIsOpen(false)} className="text-white hover:opacity-80">
              ✕
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 p-4 overflow-y-auto text-sm space-y-2">
            <p className="text-gray-600 italic mb-2">
              Pose-moi une question sur Cemade, je suis là pour t'aider ! 💬
            </p>
            {history.map((msg, index) => (
              <p
                key={index}
                className={
                  msg.from === 'user'
                    ? 'text-right text-orangeCustom'
                    : 'text-left text-gray-800'
                }
              >
                {msg.text}
              </p>
            ))}
            {isLoading && (
              <p className="text-orangeCustom animate-pulse text-left">...<FontAwesomeIcon icon={faBrain} color='pink' /></p>
            )}

            {/* Ancre invisible pour le scroll */}
            <div ref={bottomRef} />
          </div>

          {/* Champ de saisie */}
          <div className="p-2 border-t flex items-center gap-2">
            <input
              id='input-ia-text-user'
              type="text"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orangeCustom"
              placeholder="Ex : C’est quoi Cemade ?"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            />
            <button
              type='button'
              onClick={handleSend}
              className="bg-orangeCustom text-white px-3 py-1 rounded-md hover:opacity-90 transition text-sm"
              disabled={isLoading}
            >
              Envoyer
            </button>
          </div>

          {/* Mention IA */}
          <div className="px-3 py-1 text-[11px] text-gray-500 text-center bg-gray-100 border-t">
             Réponses générées automatiquement par une intelligence artificielle et peuvent ne pas être exactes à 100 %.
          </div>
        </div>
      )}
    </div>
  );
};

export default AssistantCemade;
